"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { platformFetch } from "./browser";
import {
  QUALIFIED_PAGE_SIZE,
  type QualifiedBetsPage,
  type RaceEntry,
  type VolumeEntry,
} from "@/lib/leaderboards/format";

/* Race boards, read the way the whitelabel reads them: from the browser,
   every five seconds, so standings move while the page is open. A race is
   scored as bets settle, and a board fetched once on the server and cached
   for a minute sat frozen next to a live feed that plainly kept moving. The
   server still renders the first frame — that is the `initial` data — so
   nothing flashes; the browser takes over from there. */

export const RACE_REFETCH_MS = 5000;
const RACE_STALE_MS = 2000;

export const useRaceLeaderboard = (promotionRaceId: string, initial?: RaceEntry[]) =>
  useQuery({
    queryKey: ["leaderboards", "race", promotionRaceId],
    queryFn: () => platformFetch<{ data: RaceEntry[] }>(`leaderboards/races/${promotionRaceId}`),
    refetchInterval: RACE_REFETCH_MS,
    staleTime: RACE_STALE_MS,
    initialData: initial ? { data: initial } : undefined,
  });

/* Only the newest page is live. A page the reader has turned back to would
   otherwise shift under them as new bets arrive at the front. */
export const useRaceQualifiedBets = (
  promotionRaceId: string,
  cursor: string | undefined,
  initial?: QualifiedBetsPage,
) =>
  useQuery({
    queryKey: ["leaderboards", "race", promotionRaceId, "qualified-bets", cursor ?? "first"],
    queryFn: () =>
      platformFetch<QualifiedBetsPage>(`leaderboards/races/${promotionRaceId}/qualified-bets`, {
        cursor,
        limit: QUALIFIED_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: cursor ? false : RACE_REFETCH_MS,
    staleTime: RACE_STALE_MS,
    initialData: initial && !cursor ? initial : undefined,
  });

export const useVolumeLeaderboard = (initial?: VolumeEntry[]) =>
  useQuery({
    queryKey: ["leaderboards", "volume"],
    queryFn: () => platformFetch<{ data: VolumeEntry[] }>("leaderboards/volume"),
    refetchInterval: 15_000,
    staleTime: 5_000,
    initialData: initial ? { data: initial } : undefined,
  });
